import {
  Chain,
  isMonacoChain,
  MonacoChain,
} from "@nitro-ape/shared/models/enums/chain";
import { MonacoSDK, resolveApiUrl } from "@0xmonaco/core";
import {
  Candlestick,
  Network,
  TradingMode,
  TradingPair,
} from "@0xmonaco/types";
import { getEnv } from "../../../platform/env/env";

/**
 * Converts Monaco symbol from API format to URL-safe format.
 * Example: "BTC/USDC" → "BTC-USDC"
 */
export const formatMonacoSymbolForUrl = (symbol: string): string => {
  return symbol.replace(/\//g, "-");
};

/**
 * Converts Monaco symbol from URL format back to API format.
 * Example: "BTC-USDC" → "BTC/USDC"
 */
export const parseMonacoSymbolFromUrl = (urlSymbol: string): string => {
  return urlSymbol.replace(/-/g, "/");
};

export const getTradingModeForChain = (chain: MonacoChain): TradingMode => {
  return chain === Chain.monaco_spot ? "SPOT" : "MARGIN";
};

export const assertMonacoChain = (chain: Chain): MonacoChain => {
  if (!isMonacoChain(chain)) {
    throw new Error(`Unsupported Monaco chain: ${chain}`);
  }
  return chain;
};

export const getMonacoAccountAddress = (sdk: MonacoSDK | null) => {
  if (!sdk) return null;
  try {
    return sdk.getAccountAddress();
  } catch {
    return sdk.walletClient?.account?.address ?? null;
  }
};

/**
 * Parses Monaco's `min_order_size` / `max_order_size` (numeric strings, in
 * base token units) into JS numbers. Returns `null` for either bound when
 * the pair is missing or the value is not a finite number — callers should
 * treat `null` as "unbounded" rather than 0.
 */
export const getMonacoOrderSizeBounds = (
  pair: Pick<TradingPair, "min_order_size" | "max_order_size"> | null | undefined,
): { minOrderSize: number | null; maxOrderSize: number | null } => {
  if (!pair) return { minOrderSize: null, maxOrderSize: null };

  const parse = (raw: string | null | undefined): number | null => {
    if (raw === null || raw === undefined || raw === "") return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  };

  return {
    minOrderSize: parse(pair.min_order_size),
    maxOrderSize: parse(pair.max_order_size),
  };
};

/**
 * Fetches candlesticks for a Monaco trading pair by asset ID.
 *
 * Uses Monaco's TradingView-recommended backward pagination pattern
 * (`end_time` + `limit`) which returns the most recent `limit` bars at or
 * before `end_time`. Passing both `start_time` and `end_time` is "capped at
 * limit" server-side and can drop the most recent bars on higher timeframes
 * (e.g. 4h, 1d) where the requested range spans more than ~350 candles.
 *
 * @param assetId - The trading pair ID (UUID format)
 * @param interval - Candlestick interval (e.g., "1m", "1h", "4h", "1d")
 * @param endTime - End time in milliseconds (most recent bar boundary)
 * @param limit - Maximum number of bars to return (server max 500, default 350)
 */
export const getMonacoCandlesticks = async (
  assetId: string,
  interval: string,
  endTime: number,
  limit?: number,
): Promise<Candlestick[]> => {
  const network = getEnv.VITE_MONACO_NETWORK as Network;
  const baseUrl = resolveApiUrl(network);
  const params = new URLSearchParams();
  params.append("end_time", Math.floor(endTime).toString());
  if (limit !== undefined) {
    const cappedLimit = Math.max(1, Math.min(500, Math.floor(limit)));
    params.append("limit", cappedLimit.toString());
  }

  const url = `${baseUrl}/api/v1/market/pairs/charts/candlestick/${assetId}/${interval}?${params.toString()}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch candlesticks: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || [];
};
